import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import {
  adminUserRowName,
  effectiveAdminUserFilter,
  joinAdminUserRows,
  matchesAdminUserRow,
  type AdminUserFilter,
  type AdminUserRow,
  type AiOperation,
} from "@winche/shared";
import { Badge, Button, Callout, Icon, Input } from "@winche/ui";
import { EmptyState, ErrorMessage, LoadButton, LoadingBlock, Metric, MetricGrid, SectionHeader, Segmented } from "../components/ui";
import { failureTitle, type AdminFailure } from "../lib/callable-error";
import { DataTable, type Column } from "../components/table";
import { compactNum, num, relativeTime, toMillis, usd, usdUnit } from "../lib/format";
import { useNow } from "../lib/hooks";

export interface UserTokenStat {
  uid: string;
  displayName: string;
  isDeleted: boolean;
  totalTokens: number;
  totalEstimatedCostUsd: number;
  totalFullPriceCostUsd: number;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  operationCounts: Partial<Record<AiOperation, number>>;
}

// Mirrored from packages/functions/src/http/admin-insights.ts (getAdminUsersInsights)
export interface AdminUserInsight {
  uid: string;
  displayName: string;
  email: string | null;
  plan: "free" | "pro";
  proMode: "search" | "radar" | null;
  onboardingCompleted: boolean;
  onboardingStep: number;
  stuckInOnboarding: boolean;
  lastActiveAt: string | null;
  createdAt: string | null;
}

interface Props {
  /** `getAdminUserStats`: accounts that used AI. Null until loaded or when it failed. */
  stats: UserTokenStat[] | null;
  /** `getAdminUsersInsights`: every account. Null until loaded or when it failed. */
  insights: AdminUserInsight[] | null;
  loading: boolean;
  error: AdminFailure | null;
  /** The insights failed: plan and onboarding columns are unavailable (never "0 PRO"). */
  insightsError?: AdminFailure | null;
  onLoad: () => void;
}

type Row = AdminUserRow<UserTokenStat, AdminUserInsight>;

/**
 * Every account (ux-admin-05): the rows are the accounts from the insights,
 * joined by uid with their AI usage, so someone stuck before their first AI
 * call is found by email with "No AI usage" instead of missing.
 */
export function UsersSection({ stats, insights, loading, error, insightsError = null, onLoad }: Props) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onLoad(); }, []);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<AdminUserFilter>("all");
  const now = useNow();

  const allRows: Row[] = useMemo(() => joinAdminUserRows(stats, insights), [stats, insights]);
  // «No AI usage» needs the stats and «Stuck» the insights: without its list a filter shows All and is disabled.
  const statsLoaded = stats !== null;
  const insightsLoaded = insights !== null;
  const shownFilter = effectiveAdminUserFilter(filter, { stats: statsLoaded, insights: insightsLoaded });
  const rows: Row[] = useMemo(
    () => allRows.filter((r) => matchesAdminUserRow(r, search, filter, { stats: statsLoaded, insights: insightsLoaded })),
    [allRows, search, filter, statsLoaded, insightsLoaded],
  );
  const stuckUsers = useMemo(() => (insights ?? []).filter((i) => i.stuckInOnboarding), [insights]);
  const noAiUsage = stats ? allRows.filter((r) => !r.stat).length : null;

  const loaded = stats !== null || insights !== null;
  const totalCost = (stats ?? []).reduce((s, u) => s + u.totalEstimatedCostUsd, 0);
  const proUsers = insights ? insights.filter((i) => i.plan === "pro").length : null;
  const top = [...(stats ?? [])].sort((a, b) => b.totalEstimatedCostUsd - a.totalEstimatedCostUsd)[0];
  /** A missing stat means "no AI usage" only when the stats loaded. */
  const noUsageLabel = stats ? "No AI usage" : "—";

  const columns: Column<Row>[] = [
    {
      key: "user",
      header: "User",
      cell: (r) => (
        <span className="flex min-w-0 flex-col">
          <span className="flex items-center gap-1.5">
            <span className={r.stat?.isDeleted ? "text-muted-foreground" : undefined}>{adminUserRowName(r)}</span>
            {r.stat?.isDeleted && (
              <Badge tone="neutral" size="sm">
                deleted
              </Badge>
            )}
          </span>
          {r.insight?.email ? <span className="text-caption text-muted-foreground">{r.insight.email}</span> : null}
        </span>
      ),
      sortValue: (r) => adminUserRowName(r).toLowerCase(),
    },
    {
      key: "plan",
      header: "Plan",
      cell: (r) =>
        !r.insight ? (
          <span className="text-subtle-foreground">—</span>
        ) : r.insight.plan === "pro" ? (
          <Badge tone="brand" size="sm">
            PRO{r.insight.proMode ? ` ${r.insight.proMode}` : ""}
          </Badge>
        ) : (
          <Badge tone="neutral" size="sm">
            Free
          </Badge>
        ),
      sortValue: (r) => (r.insight ? (r.insight.plan === "pro" ? 1 : 0) : null),
    },
    {
      key: "active",
      header: "Last active",
      cell: (r) => {
        const ms = toMillis(r.insight?.lastActiveAt ?? null);
        const rel = relativeTime(ms, now);
        return (
          <span className="whitespace-nowrap text-muted-foreground" title={rel.title}>
            {r.insight ? (ms ? rel.label : "never") : "—"}
          </span>
        );
      },
      sortValue: (r) => toMillis(r.insight?.lastActiveAt ?? null),
    },
    {
      key: "onboarding",
      header: "Onboarding",
      cell: (r) =>
        !r.insight ? (
          <span className="text-subtle-foreground">—</span>
        ) : r.insight.onboardingCompleted ? (
          <span className="text-muted-foreground">Done</span>
        ) : r.insight.stuckInOnboarding ? (
          <Badge tone="warning" size="sm">
            Stuck · step {r.insight.onboardingStep}
          </Badge>
        ) : (
          <span className="text-muted-foreground">Step {r.insight.onboardingStep}</span>
        ),
      sortValue: (r) => (r.insight ? (r.insight.onboardingCompleted ? 4 : r.insight.onboardingStep) : null),
    },
    {
      key: "cost",
      header: "AI cost",
      align: "right",
      cell: (r) =>
        r.stat ? (
          usdUnit(r.stat.totalEstimatedCostUsd)
        ) : (
          <span className="whitespace-nowrap text-subtle-foreground">{noUsageLabel}</span>
        ),
      sortValue: (r) => r.stat?.totalEstimatedCostUsd ?? null,
      footer: usd(rows.reduce((s, r) => s + (r.stat?.totalEstimatedCostUsd ?? 0), 0)),
    },
    {
      key: "full",
      header: "At retail",
      title: "Cost if every call had been billed at retail price",
      align: "right",
      cell: (r) => <span className="text-muted-foreground">{r.stat ? usdUnit(r.stat.totalFullPriceCostUsd) : "—"}</span>,
      sortValue: (r) => r.stat?.totalFullPriceCostUsd ?? null,
    },
    {
      key: "tokens",
      header: "Tokens",
      align: "right",
      cell: (r) => (r.stat ? compactNum(r.stat.totalTokens) : "—"),
      sortValue: (r) => r.stat?.totalTokens ?? null,
      footer: compactNum(rows.reduce((s, r) => s + (r.stat?.totalTokens ?? 0), 0)),
    },
    {
      key: "prompt",
      header: "Prompt",
      align: "right",
      cell: (r) => (r.stat ? compactNum(r.stat.totalPromptTokens) : "—"),
      sortValue: (r) => r.stat?.totalPromptTokens ?? null,
    },
    {
      key: "completion",
      header: "Completion",
      align: "right",
      cell: (r) => (r.stat ? compactNum(r.stat.totalCompletionTokens) : "—"),
      sortValue: (r) => r.stat?.totalCompletionTokens ?? null,
    },
  ];

  return (
    <div>
      <SectionHeader
        title="Users"
        subtitle="Every account with its plan, onboarding and AI usage."
        action={<LoadButton loading={loading} loaded={loaded} onLoad={onLoad} />}
      />

      {error && <ErrorMessage failure={error} onRetry={onLoad} retrying={loading} />}
      {insightsError && (
        <Callout
          tone="warning"
          className="mb-4"
          title="Plan/onboarding columns unavailable"
          action={
            <Button variant="outline" size="sm" onClick={onLoad} loading={loading}>
              Retry
            </Button>
          }
        >
          {failureTitle(insightsError)}
          {insightsError.detail ? ` — ${insightsError.detail}` : ""}. Only accounts with AI usage are listed, and plan,
          onboarding and last activity show —.
        </Callout>
      )}

      {stuckUsers.length > 0 && (
        <Callout tone="warning" className="mb-4" title={`${stuckUsers.length} user${stuckUsers.length === 1 ? "" : "s"} stuck in onboarding`}>
          More than 48 h without finishing: {stuckUsers.slice(0, 5).map((s) => s.displayName).join(", ")}
          {stuckUsers.length > 5 ? ` and ${stuckUsers.length - 5} more` : ""}.
        </Callout>
      )}

      {!loaded && loading && <LoadingBlock metrics={4} rows={8} label="Loading users" />}
      {!loaded && !loading && !error && !insightsError && <EmptyState message="No users loaded." />}
      {loaded && allRows.length === 0 && <EmptyState message="No accounts yet." />}

      {loaded && allRows.length > 0 && (
        <>
          <MetricGrid cols={4} className="mb-6">
            <Metric label="Accounts" value={insights ? num(insights.length) : "—"} sub={insights ? undefined : "Insights unavailable"} />
            <Metric
              label="With AI usage"
              value={stats ? num(stats.length) : "—"}
              sub={noAiUsage != null && insights ? `${num(noAiUsage)} with no AI usage` : undefined}
            />
            <Metric label="PRO users" value={proUsers != null ? num(proUsers) : "—"} />
            <Metric
              label="AI cost, all users"
              value={stats ? usd(totalCost) : "—"}
              sub={top ? `Top: ${top.displayName} ${usdUnit(top.totalEstimatedCostUsd)}` : undefined}
            />
          </MetricGrid>

          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex w-full flex-wrap items-center gap-3 md:w-auto">
              <div className="relative w-full max-w-xs">
                <Icon icon={Search} size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  aria-label="Search users by name, email or uid"
                  placeholder="Search name, email or uid"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Segmented
                label="Show"
                value={shownFilter}
                onChange={setFilter}
                options={[
                  { value: "all", label: "All" },
                  { value: "stuck", label: `Stuck${insights ? ` (${num(stuckUsers.length)})` : ""}`, disabled: !insightsLoaded },
                  {
                    value: "no_ai_usage",
                    label: `No AI usage${noAiUsage != null && insights ? ` (${num(noAiUsage)})` : ""}`,
                    disabled: !statsLoaded,
                  },
                ]}
              />
            </div>
            <p className="text-caption text-muted-foreground">
              {rows.length === allRows.length ? `${num(allRows.length)} users` : `${num(rows.length)} of ${num(allRows.length)} users`}
            </p>
          </div>
          <DataTable
            caption="Users"
            columns={columns}
            rows={rows}
            rowKey={(r) => r.uid}
            defaultSort={{ key: "cost", dir: "desc" }}
            maxHeight={640}
            emptyMessage={shownFilter === "all" ? "No user matches the search." : "No user matches the search and filter."}
          />
        </>
      )}
    </div>
  );
}
